import { useEffect, useState } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, Repeat } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [availableMonths, setAvailableMonths] = useState([]);
  
  const incomeCategories = [
    'Salário', 'Comissões', 'Freelance / trabalhos extras', 'Venda de produtos ou serviços', 
    'Dividendos', 'Prêmios / sorteios', 'Outros'
  ];

  const expenseCategories = [
    'Aluguel', 'Prestação da casa', 'Condomínio', 'IPTU', 'Seguro residencial',
    'Conta de luz', 'Conta de água', 'Gás', 'Internet', 'Telefone / celular', 'TV por assinatura',
    'Supermercado', 'Feira', 'Padaria', 'Restaurantes', 'Delivery',
    'Combustível', 'Transporte público', 'Aplicativos (Uber, 99, etc.)', 'Seguro do carro',
    'Plano de saúde', 'Medicamentos', 'Consultas médicas', 'Exames', 'Dentista',
    'Academia', 'Outros'
  ];

  const [newTransaction, setNewTransaction] = useState({
    type: 'expense',
    amount: '',
    category: 'Outros',
    description: '',
    date: '',
    recurrence: 'pontual'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, accRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true })
      ]);
      setTransactions(transRes.data);
      setAccounts(accRes.data);
      
      // Extract unique months from transactions
      const months = {};
      transRes.data.forEach(t => {
        try {
          const date = new Date(t.date);
          const monthKey = date.toISOString().slice(0, 7); // YYYY-MM
          const monthLabel = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
          months[monthKey] = monthLabel;
        } catch (e) {}
      });
      
      const sortedMonths = Object.keys(months).sort().reverse().map(key => ({
        value: key,
        label: months[key].charAt(0).toUpperCase() + months[key].slice(1)
      }));
      
      setAvailableMonths(sortedMonths);
      setFilteredTransactions(transRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth === 'all') {
      setFilteredTransactions(transactions);
    } else {
      const filtered = transactions.filter(t => {
        try {
          const date = new Date(t.date);
          const monthKey = date.toISOString().slice(0, 7);
          return monthKey === selectedMonth;
        } catch {
          return false;
        }
      });
      setFilteredTransactions(filtered);
    }
  }, [selectedMonth, transactions]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${BACKEND_URL}/api/transactions`, {
        type: newTransaction.type,
        amount: parseFloat(newTransaction.amount),
        category: newTransaction.category,
        description: newTransaction.description || `Transação ${newTransaction.type === 'income' ? 'Receita' : 'Despesa'}`,
        date: newTransaction.date || new Date().toISOString(),
        recurrence: newTransaction.recurrence
      }, { withCredentials: true });
      toast.success('Transação criada!');
      setOpen(false);
      fetchData();
      setNewTransaction({ type: 'expense', amount: '', category: 'Outros', description: '', date: '', recurrence: 'pontual' });
    } catch (error) {
      toast.error('Erro ao criar transação');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction({
      ...transaction,
      date: transaction.date ? transaction.date.split('T')[0] : ''
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!editingTransaction) return;
    
    try {
      await axios.patch(`${BACKEND_URL}/api/transactions/${editingTransaction.transaction_id}`, {
        type: editingTransaction.type,
        amount: parseFloat(editingTransaction.amount),
        category: editingTransaction.category,
        description: editingTransaction.description,
        date: editingTransaction.date || new Date().toISOString(),
        recurrence: editingTransaction.recurrence
      }, { withCredentials: true });
      toast.success('Transação atualizada!');
      setEditOpen(false);
      setEditingTransaction(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar transação');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, { withCredentials: true });
      toast.success('Transação excluída');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  if (loading) return <LoadingBar text="Carregando transações..." />;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-2 text-gradient-green">Transações</h1>
          <p className="text-[var(--neo-text-light)]">Gerencie suas receitas e despesas</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="neo-select w-full sm:w-[200px]" data-testid="month-filter">
              <SelectValue placeholder="Filtrar por mês" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os meses</SelectItem>
              {availableMonths.map(month => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="neo-btn flex items-center" data-testid="add-transaction-button">
                <Plus size={20} className="mr-2" />Nova Transação
              </button>
            </DialogTrigger>
            <DialogContent className="neo-card border-none max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[var(--neo-text-dark)]">Nova Transação</DialogTitle>
                <DialogDescription className="text-[var(--neo-text-light)]">Adicione uma nova receita ou despesa</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Tipo</label>
                  <Select value={newTransaction.type} onValueChange={(val) => setNewTransaction({...newTransaction, type: val, category: val === 'income' ? 'Salário' : 'Outros'})}>
                    <SelectTrigger className="neo-input" data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">🟢 Receita</SelectItem>
                      <SelectItem value="expense">🔴 Despesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Categoria</label>
                  <Select value={newTransaction.category} onValueChange={(val) => setNewTransaction({...newTransaction, category: val})}>
                    <SelectTrigger className="neo-input" data-testid="transaction-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(newTransaction.type === 'income' ? incomeCategories : expenseCategories).map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Valor</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={newTransaction.amount} 
                    onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})} 
                    data-testid="transaction-amount" 
                    required 
                    placeholder="0.00"
                    className="neo-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Recorrência</label>
                  <Select value={newTransaction.recurrence} onValueChange={(val) => setNewTransaction({...newTransaction, recurrence: val})}>
                    <SelectTrigger className="neo-input" data-testid="transaction-recurrence-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pontual">Pontual (avulso)</SelectItem>
                      <SelectItem value="recorrente_mensal">🔄 Recorrente Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Descrição</label>
                  <input 
                    value={newTransaction.description} 
                    onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})} 
                    data-testid="transaction-description" 
                    placeholder="Ex: Pagamento freelance" 
                    className="neo-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Data</label>
                  <input 
                    type="date" 
                    value={newTransaction.date} 
                    onChange={(e) => setNewTransaction({...newTransaction, date: e.target.value})} 
                    data-testid="transaction-date" 
                    className="neo-input"
                  />
                </div>
                <button type="submit" className="neo-btn w-full" data-testid="submit-transaction">
                  Criar Transação
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Bar */}
      {filteredTransactions.length > 0 && (
        <div className="neo-card-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm font-medium text-[var(--neo-text)]">
            Exibindo {filteredTransactions.length} {filteredTransactions.length === 1 ? 'transação' : 'transações'}
            {selectedMonth !== 'all' && ` em ${availableMonths.find(m => m.value === selectedMonth)?.label}`}
          </p>
          <div className="flex space-x-6 text-sm">
            <span className="font-semibold text-[var(--neo-green)]">
              ↑ Receitas: R$ {filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
            <span className="font-semibold text-[var(--neo-red)]">
              ↓ Despesas: R$ {filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Transactions List */}
      <div className="space-y-4">
        {filteredTransactions.length === 0 ? (
          <div className="neo-card text-center py-12">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--neo-bg)' }}>
              <ArrowUp size={32} className="text-[var(--neo-text-light)] opacity-50" />
            </div>
            <p className="text-[var(--neo-text-light)]">
              {selectedMonth === 'all' ? 'Nenhuma transação encontrada' : 'Nenhuma transação neste mês'}
            </p>
            <p className="text-sm text-[var(--neo-text-light)] mt-2">Clique em "Nova Transação" para começar!</p>
          </div>
        ) : (
          filteredTransactions.map(t => (
            <div key={t.transaction_id} className="neo-card" data-testid={`transaction-${t.transaction_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div 
                    className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0 ${
                      t.type === 'income' 
                        ? 'bg-[var(--neo-green)]/10' 
                        : 'bg-[var(--neo-red)]/10'
                    }`}
                  >
                    {t.type === 'income' ? (
                      <ArrowUp size={22} weight="bold" className="text-[var(--neo-green)]" />
                    ) : (
                      <ArrowDown size={22} weight="bold" className="text-[var(--neo-red)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[var(--neo-text-dark)] truncate">{t.description}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="neo-badge text-xs">{t.category}</span>
                      <span className="text-sm text-[var(--neo-text-light)]">
                        {new Date(t.date).toLocaleDateString('pt-BR')}
                      </span>
                      {t.recurrence === 'recorrente_mensal' && (
                        <span className="neo-badge-green text-xs flex items-center gap-1">
                          <Repeat size={12} /> Recorrente
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4 pl-13 sm:pl-0">
                  <p className={`text-lg sm:text-2xl font-bold tabular-nums ${
                    t.type === 'income' ? 'text-[var(--neo-green)]' : 'text-[var(--neo-red)]'
                  }`}>
                    {t.type === 'income' ? '+' : '-'}R$ {t.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  
                  <div className="flex space-x-2">
                    <button 
                      className="neo-icon-btn w-9 h-9 sm:w-10 sm:h-10"
                      onClick={() => handleEdit(t)}
                      data-testid={`edit-transaction-${t.transaction_id}`}
                      title="Editar transação"
                    >
                      <PencilSimple size={16} className="text-[var(--neo-gold)]" />
                    </button>
                    <button 
                      className="neo-icon-btn w-9 h-9 sm:w-10 sm:h-10"
                      onClick={() => handleDelete(t.transaction_id)} 
                      data-testid={`delete-transaction-${t.transaction_id}`}
                      title="Excluir transação"
                    >
                      <Trash size={16} className="text-[var(--neo-red)]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Transaction Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="neo-card border-none max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--neo-text-dark)] flex items-center gap-2">
              <PencilSimple size={24} className="text-[var(--neo-gold)]" />
              Editar Transação
            </DialogTitle>
            <DialogDescription className="text-[var(--neo-text-light)]">
              Altere os dados da transação
            </DialogDescription>
          </DialogHeader>
          {editingTransaction && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Tipo</label>
                <Select 
                  value={editingTransaction.type} 
                  onValueChange={(val) => setEditingTransaction({
                    ...editingTransaction, 
                    type: val, 
                    category: val === 'income' ? 'Salário' : 'Outros'
                  })}
                >
                  <SelectTrigger className="neo-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">🟢 Receita</SelectItem>
                    <SelectItem value="expense">🔴 Despesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Categoria</label>
                <Select 
                  value={editingTransaction.category} 
                  onValueChange={(val) => setEditingTransaction({...editingTransaction, category: val})}
                >
                  <SelectTrigger className="neo-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(editingTransaction.type === 'income' ? incomeCategories : expenseCategories).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Valor</label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={editingTransaction.amount} 
                  onChange={(e) => setEditingTransaction({...editingTransaction, amount: e.target.value})} 
                  required 
                  className="neo-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Recorrência</label>
                <Select 
                  value={editingTransaction.recurrence} 
                  onValueChange={(val) => setEditingTransaction({...editingTransaction, recurrence: val})}
                >
                  <SelectTrigger className="neo-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pontual">Pontual (avulso)</SelectItem>
                    <SelectItem value="recorrente_mensal">🔄 Recorrente Mensal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Descrição</label>
                <input 
                  value={editingTransaction.description} 
                  onChange={(e) => setEditingTransaction({...editingTransaction, description: e.target.value})} 
                  placeholder="Ex: Pagamento freelance" 
                  className="neo-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--neo-text)] mb-1 block">Data</label>
                <input 
                  type="date" 
                  value={editingTransaction.date} 
                  onChange={(e) => setEditingTransaction({...editingTransaction, date: e.target.value})} 
                  className="neo-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  className="neo-btn-outline flex-1"
                  onClick={() => setEditOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="neo-btn flex-1">
                  Salvar Alterações
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
